import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { X, Calculator, Package, Plane, MapPin, Home } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import HighTechPricing from './HighTechPricing';

const PricingCalculator = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const [destination, setDestination] = useState('');
  const [homeDelivery, setHomeDelivery] = useState('');
  const [weight, setWeight] = useState('');
  const [totalPrice, setTotalPrice] = useState(null);

  const translations = {
    fr: {
      title: 'Calculateur de prix',
      subtitle: 'Obtenez un devis instantané pour votre envoi',
      destination: 'Sélectionner la destination',
      selectDestination: 'Veuillez sélectionner une destination',
      homeDelivery: 'Livraison à domicile ?',
      selectAnswer: 'Veuillez sélectionner une réponse',
      yes: 'Oui',
      no: 'Non',
      weight: 'Poids du colis (en KG)',
      weightPlaceholder: 'Entrez le poids',
      total: 'Le total à payer sera de :',
      note16kg: 'Si votre colis atteint 16 KG, veuillez SVP lire la section "Tarifications des colis de 23 KG" pour un meilleur prix.',
      bulk23Title: 'Tarifications des colis de 23 KG',
      bulk23Kin: "D'Ottawa/Montréal à Kinshasa: 460 $CAD (plus taxes)",
      bulk23Other: "D'Ottawa/Montréal aux autres villes de la RDC: 500 $CAD (plus taxes)",
      includes: 'Ce prix inclut:',
      includesList: [
        'Ramassage à domicile (Ottawa/Montréal)',
        'Emballage du colis',
        'Frais de douanes',
        'Livraison à domicile (Kinshasa/Lubumbashi)'
      ],
      destinations: {
        'ottawa-kinshasa': 'Ottawa/Montréal vers Kinshasa',
        'ottawa-rdc': 'Ottawa/Montréal vers les autres villes de la RDC',
        'paris-kinshasa': 'Paris Vers Kinshasa',
        'kinshasa-ottawa': 'Kinshasa vers Ottawa/Montréal',
        'kinshasa-paris': 'Kinshasa Vers Paris',
        'geneve-kinshasa': 'Genève vers Kinshasa',
        'kinshasa-geneve': 'Kinshasa vers Genève'
      },
      close: 'Fermer'
    },
    en: {
      title: 'Price Calculator',
      subtitle: 'Get an instant quote for your shipment',
      destination: 'Select destination',
      selectDestination: 'Please select a destination',
      homeDelivery: 'Home delivery?',
      selectAnswer: 'Please select an answer',
      yes: 'Yes',
      no: 'No',
      weight: 'Package weight (in KG)',
      weightPlaceholder: 'Enter weight',
      total: 'Total amount to pay:',
      note16kg: 'If your package reaches 16 KG, please read the "23 KG Package Pricing" section for a better price.',
      bulk23Title: '23 KG Package Pricing',
      bulk23Kin: "Ottawa/Montreal to Kinshasa: $460 CAD (plus taxes)",
      bulk23Other: "Ottawa/Montreal to other DRC cities: $500 CAD (plus taxes)",
      includes: 'This price includes:',
      includesList: [
        'Home pickup (Ottawa/Montreal)',
        'Package wrapping',
        'Customs fees',
        'Home delivery (Kinshasa/Lubumbashi)'
      ],
      destinations: {
        'ottawa-kinshasa': 'Ottawa/Montreal to Kinshasa',
        'ottawa-rdc': 'Ottawa/Montreal to other DRC cities',
        'paris-kinshasa': 'Paris to Kinshasa',
        'kinshasa-ottawa': 'Kinshasa to Ottawa/Montreal',
        'kinshasa-paris': 'Kinshasa to Paris',
        'geneve-kinshasa': 'Geneva to Kinshasa',
        'kinshasa-geneve': 'Kinshasa to Geneva'
      },
      close: 'Close'
    }
  };

  const t = translations[language] || translations.en;

  // Exact pricing rates from the original website
  const prixParKilosOttawaVersKin = 33.90;
  const prixParKilosParisVersKin = 20;
  const prixParKilosKinshasaVersOttawa = 30;
  const prixParKilosKinshasaVersParis = 20;
  const prixParKilosGeneveVersKin = 30;
  const fraisDeTransportPourLubumbashi = 10;

  const calculatePrice = () => {
    if (!destination || !homeDelivery || !weight) {
      setTotalPrice(null);
      return;
    }

    const nombreDeKilos = parseFloat(weight);
    if (isNaN(nombreDeKilos) || nombreDeKilos <= 0) {
      setTotalPrice(null);
      return;
    }

    let price = 0;
    let currency = '$CAD';
    const isHomeDelivery = homeDelivery === 'yes';

    // Discount calculations
    const pourcentageOttawaVersKinPlusDe25KG = (nombreDeKilos * prixParKilosOttawaVersKin) * 0.30;
    const pourcentageKinshasaVersOttawaPlusDe25KG = (nombreDeKilos * prixParKilosKinshasaVersOttawa) * 0.30;
    const pourcentageOttawaVersKin = (nombreDeKilos * prixParKilosOttawaVersKin) * 0.06;
    const pourcentageKinshasaVersOttawa = (nombreDeKilos * prixParKilosKinshasaVersOttawa) * 0.06;

    switch (destination) {
      case 'ottawa-kinshasa':
        currency = '$CAD';
        if (isHomeDelivery) {
          if (nombreDeKilos > 22) {
            price = ((nombreDeKilos * prixParKilosOttawaVersKin) - pourcentageOttawaVersKinPlusDe25KG) + 15;
          } else if (nombreDeKilos >= 1) {
            if (nombreDeKilos >= 5) {
              price = ((nombreDeKilos * prixParKilosOttawaVersKin) - pourcentageOttawaVersKin) + 10;
            } else {
              price = (nombreDeKilos * prixParKilosOttawaVersKin) + 10;
            }
          } else {
            price = prixParKilosOttawaVersKin + 10;
          }
        } else {
          if (nombreDeKilos > 22) {
            price = (nombreDeKilos * prixParKilosOttawaVersKin) - pourcentageOttawaVersKinPlusDe25KG;
          } else if (nombreDeKilos >= 1) {
            if (nombreDeKilos >= 5) {
              price = (nombreDeKilos * prixParKilosOttawaVersKin) - pourcentageOttawaVersKin;
            } else {
              price = nombreDeKilos * prixParKilosOttawaVersKin;
            }
          } else {
            price = prixParKilosOttawaVersKin;
          }
        }
        break;

      case 'ottawa-rdc':
        currency = '$CAD';
        // Same as Kinshasa calculation first
        if (isHomeDelivery) {
          if (nombreDeKilos > 22) {
            price = ((nombreDeKilos * prixParKilosOttawaVersKin) - pourcentageOttawaVersKinPlusDe25KG) + 15;
          } else if (nombreDeKilos >= 1) {
            if (nombreDeKilos >= 5) {
              price = ((nombreDeKilos * prixParKilosOttawaVersKin) - pourcentageOttawaVersKin) + 10;
            } else {
              price = (nombreDeKilos * prixParKilosOttawaVersKin) + 10;
            }
          } else {
            price = prixParKilosOttawaVersKin + 10;
          }
        } else {
          if (nombreDeKilos > 22) {
            price = (nombreDeKilos * prixParKilosOttawaVersKin) - pourcentageOttawaVersKinPlusDe25KG;
          } else if (nombreDeKilos >= 1) {
            if (nombreDeKilos >= 5) {
              price = (nombreDeKilos * prixParKilosOttawaVersKin) - pourcentageOttawaVersKin;
            } else {
              price = nombreDeKilos * prixParKilosOttawaVersKin;
            }
          } else {
            price = prixParKilosOttawaVersKin;
          }
        }
        // Add transport fee for other RDC cities
        price = price + (fraisDeTransportPourLubumbashi * nombreDeKilos);
        break;

      case 'paris-kinshasa':
        currency = '€';
        if (isHomeDelivery) {
          if (nombreDeKilos > 22) {
            price = (nombreDeKilos * prixParKilosParisVersKin) + 15;
          } else if (nombreDeKilos >= 1) {
            price = (nombreDeKilos * prixParKilosParisVersKin) + 10;
          } else {
            price = prixParKilosParisVersKin + 10;
          }
        } else {
          if (nombreDeKilos >= 1) {
            price = nombreDeKilos * prixParKilosParisVersKin;
          } else {
            price = prixParKilosParisVersKin;
          }
        }
        break;

      case 'kinshasa-ottawa':
        currency = '$US';
        if (isHomeDelivery) {
          if (nombreDeKilos > 22) {
            price = ((nombreDeKilos * prixParKilosKinshasaVersOttawa) - pourcentageKinshasaVersOttawaPlusDe25KG) + 15;
          } else if (nombreDeKilos >= 1) {
            if (nombreDeKilos >= 5) {
              price = ((nombreDeKilos * prixParKilosKinshasaVersOttawa) - pourcentageKinshasaVersOttawa) + 10;
            } else {
              price = (nombreDeKilos * prixParKilosKinshasaVersOttawa) + 10;
            }
          } else {
            price = prixParKilosKinshasaVersOttawa + 10;
          }
        } else {
          if (nombreDeKilos > 22) {
            price = (nombreDeKilos * prixParKilosKinshasaVersOttawa) - pourcentageKinshasaVersOttawaPlusDe25KG;
          } else if (nombreDeKilos >= 1) {
            if (nombreDeKilos >= 5) {
              price = (nombreDeKilos * prixParKilosKinshasaVersOttawa) - pourcentageKinshasaVersOttawa;
            } else {
              price = nombreDeKilos * prixParKilosKinshasaVersOttawa;
            }
          } else {
            price = prixParKilosKinshasaVersOttawa;
          }
        }
        break;

      case 'kinshasa-paris':
        currency = '$US';
        if (isHomeDelivery) {
          if (nombreDeKilos > 22) {
            price = (nombreDeKilos * prixParKilosKinshasaVersParis) + 15;
          } else if (nombreDeKilos >= 1) {
            price = (nombreDeKilos * prixParKilosKinshasaVersParis) + 10;
          } else {
            price = prixParKilosKinshasaVersParis + 10;
          }
        } else {
          if (nombreDeKilos >= 1) {
            price = nombreDeKilos * prixParKilosKinshasaVersParis;
          } else {
            price = prixParKilosKinshasaVersParis;
          }
        }
        break;

      case 'geneve-kinshasa':
        currency = 'CHF';
        const pourcentageGeneveVersKin = (nombreDeKilos * prixParKilosGeneveVersKin) * 0.06;
        const pourcentageGenevePlusDe25KG = (nombreDeKilos * prixParKilosGeneveVersKin) * 0.30;
        
        if (isHomeDelivery) {
          if (nombreDeKilos > 22) {
            price = ((nombreDeKilos * prixParKilosGeneveVersKin) - pourcentageGenevePlusDe25KG) + 15;
          } else if (nombreDeKilos >= 1) {
            if (nombreDeKilos >= 5) {
              price = ((nombreDeKilos * prixParKilosGeneveVersKin) - pourcentageGeneveVersKin) + 10;
            } else {
              price = (nombreDeKilos * prixParKilosGeneveVersKin) + 10;
            }
          } else {
            price = prixParKilosGeneveVersKin + 10;
          }
        } else {
          if (nombreDeKilos > 22) {
            price = (nombreDeKilos * prixParKilosGeneveVersKin) - pourcentageGenevePlusDe25KG;
          } else if (nombreDeKilos >= 1) {
            if (nombreDeKilos >= 5) {
              price = (nombreDeKilos * prixParKilosGeneveVersKin) - pourcentageGeneveVersKin;
            } else {
              price = nombreDeKilos * prixParKilosGeneveVersKin;
            }
          } else {
            price = prixParKilosGeneveVersKin;
          }
        }
        break;

      case 'kinshasa-geneve':
        currency = '$US';
        if (isHomeDelivery) {
          if (nombreDeKilos > 22) {
            price = (nombreDeKilos * prixParKilosKinshasaVersParis) + 15;
          } else if (nombreDeKilos >= 1) {
            price = (nombreDeKilos * prixParKilosKinshasaVersParis) + 10;
          } else {
            price = prixParKilosKinshasaVersParis + 10;
          }
        } else {
          if (nombreDeKilos >= 1) {
            price = nombreDeKilos * prixParKilosKinshasaVersParis;
          } else {
            price = prixParKilosKinshasaVersParis;
          }
        }
        break;

      default:
        price = 0;
    }

    setTotalPrice({
      amount: Math.round(price).toFixed(2),
      currency: currency,
      showBulkNote: nombreDeKilos >= 16 && nombreDeKilos <= 23 && 
        (destination === 'ottawa-kinshasa' || destination === 'ottawa-rdc')
    });
  };

  useEffect(() => {
    calculatePrice();
  }, [destination, homeDelivery, weight]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-cyan-600 text-white p-6 rounded-t-3xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <Calculator className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">{t.title}</h2>
              <p className="text-white/80 text-sm">{t.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Form */}
          <div className="space-y-5">
            {/* Destination */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-600" />
                {t.destination}
              </Label>
              <Select value={destination} onValueChange={setDestination}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder={t.selectDestination} />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(t.destinations).map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Home Delivery */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Home className="w-4 h-4 text-blue-600" />
                {t.homeDelivery}
              </Label>
              <Select value={homeDelivery} onValueChange={setHomeDelivery}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder={t.selectAnswer} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes">{t.yes}</SelectItem>
                  <SelectItem value="no">{t.no}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Weight */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-600" />
                {t.weight}
              </Label>
              <Input
                type="number"
                min="0.1"
                step="0.1"
                value={weight}
                onChange={(e) => setWeight(e.target.value)}
                placeholder={t.weightPlaceholder}
                className="h-12"
              />
            </div>
          </div>

          {/* Result */}
          {totalPrice && (
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-6 border border-blue-100">
              <p className="text-slate-600 mb-2 text-lg font-medium">{t.total}</p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold text-blue-600">
                  {totalPrice.amount}
                </span>
                <span className="text-xl text-slate-700 font-semibold">{totalPrice.currency}</span>
              </div>

              {/* What's included */}
              <div className="mt-4 pt-4 border-t border-blue-200">
                <p className="text-sm font-medium text-slate-700 mb-2">{t.includes}</p>
                <ul className="grid grid-cols-2 gap-2">
                  {t.includesList.map((item, index) => (
                    <li key={index} className="text-sm text-slate-600 flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="bg-orange-50 rounded-xl p-4 border border-orange-100 space-y-3">
            <p className="text-sm text-orange-800">
              {t.note16kg}
            </p>
            <div className="border-t border-orange-200 pt-3">
              <p className="text-sm font-semibold text-orange-900 mb-2">{t.bulk23Title}:</p>
              <ul className="text-sm text-orange-800 space-y-1">
                <li>• {t.bulk23Kin}</li>
                <li>• {t.bulk23Other}</li>
              </ul>
            </div>
          </div>

          {/* High-Tech Equipment Pricing */}
          <HighTechPricing />

          {/* Action button */}
          <Button
            onClick={onClose}
            className="w-full h-12 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white"
          >
            <Plane className="w-5 h-5 mr-2" />
            {t.close}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PricingCalculator;
